/**
 * Plain-language summary of an activity log row for non-technical readers.
 * Uses note / entity+action when present, otherwise infers from API path.
 */

const ACTION_PHRASE = {
    approve: 'Approved',
    reject: 'Rejected',
    suspend: 'Suspended',
    reactivate: 'Reactivated',
    edit_client_data: 'Edited client registration details',
    login: 'Signed in',
    logout: 'Signed out',
    create: 'Created',
    update: 'Updated',
    delete: 'Deleted',
};

const ENTITY_PHRASE = {
    User: 'a user account',
    FormSubmission: 'a client registration',
    Lead: 'a lead',
    Incentive: 'an incentive',
    Reimbursement: 'a reimbursement',
    Role: 'a role',
    Settings: 'app settings',
    Auth: 'the app',
};

/** Ordered patterns — first match wins. Optional `method` narrows the match. */
const PATH_RULES = [
    { re: /^\/api\/notifications\/read-all/i, text: 'Marked all notifications as read' },
    { re: /^\/api\/notifications\/[^/]+\/read$/i, text: 'Marked a notification as read' },
    { re: /^\/api\/auth\/login/i, text: 'Signed in' },
    { re: /^\/api\/auth\/signup/i, text: 'Registered a new account' },
    { re: /^\/api\/auth\/change-password/i, text: 'Changed their password' },
    { re: /^\/api\/auth\/complete-onboarding/i, text: 'Completed onboarding' },
    { re: /^\/api\/auth\/complete-training/i, text: 'Completed training' },
    { re: /^\/api\/auth\/accept-kra/i, text: 'Accepted KRA terms' },
    { re: /^\/api\/auth\/accept-kpi/i, text: 'Accepted KPI terms' },
    { re: /^\/api\/users\/[^/]+\/approve/i, text: 'Approved a pending user signup' },
    { re: /^\/api\/users\/[^/]+\/reject/i, text: 'Rejected a pending user signup' },
    { re: /^\/api\/users\/[^/]+\/suspend/i, text: 'Suspended a user account' },
    { re: /^\/api\/users\/[^/]+\/reactivate/i, text: 'Reactivated a user account' },
    { re: /^\/api\/users\/[^/]+\/reset-password/i, text: 'Reset a user password' },
    { re: /^\/api\/users\/[^/]+\/permissions/i, text: 'Updated user permissions' },
    { re: /^\/api\/users\/[^/]+$/i, method: 'PATCH', text: 'Updated a user profile' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/review/i, text: 'Reviewed a client registration' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/stage/i, text: 'Updated a client deal stage' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/status/i, text: 'Updated a client status' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/sales-team/i, text: 'Updated sales team on a client' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/data/i, text: 'Edited client registration data' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/payments\/[^/]+\/approve/i, text: 'Approved a client payment' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/payments\/[^/]+\/reject/i, text: 'Rejected a client payment' },
    { re: /^\/api\/forms\/submissions\/[^/]+\/payments/i, text: 'Added or updated a client payment' },
    { re: /^\/api\/forms\/submissions\/[^/]+/i, method: 'POST', text: 'Updated a client registration' },
    { re: /^\/api\/forms\/submissions$/i, method: 'POST', text: 'Submitted a new client registration' },
    { re: /^\/api\/forms\/schemas/i, text: 'Changed a form template' },
    { re: /^\/api\/reimbursements\/[^/]+\/review/i, text: 'Reviewed a reimbursement request' },
    { re: /^\/api\/reimbursements\/[^/]+\/cancel/i, text: 'Cancelled a reimbursement request' },
    { re: /^\/api\/reimbursements$/i, method: 'POST', text: 'Submitted a reimbursement request' },
    { re: /^\/api\/incentives\/payouts/i, text: 'Updated an incentive payout' },
    { re: /^\/api\/incentives\/rules/i, text: 'Updated incentive rules' },
    { re: /^\/api\/client-incentives\/[^/]+\/approve/i, text: 'Approved a client incentive' },
    { re: /^\/api\/client-incentives\/[^/]+\/unlock/i, text: 'Unlocked a client incentive' },
    { re: /^\/api\/client-incentives\/[^/]+\/paid/i, text: 'Marked a client incentive as paid' },
    { re: /^\/api\/client-incentives\/confirm/i, text: 'Confirmed a client incentive' },
    { re: /^\/api\/refunds\/[^/]+\/approve/i, text: 'Approved a refund request' },
    { re: /^\/api\/refunds\/[^/]+\/reject/i, text: 'Rejected a refund request' },
    { re: /^\/api\/refunds/i, method: 'POST', text: 'Requested a refund' },
    { re: /^\/api\/achievers/i, text: 'Updated achievers content' },
    { re: /^\/api\/roles/i, text: 'Updated roles or permissions' },
    { re: /^\/api\/settings/i, text: 'Changed application settings' },
    { re: /^\/api\/files/i, text: 'Updated files or downloads' },
    { re: /^\/api\/downloads/i, text: 'Updated download resources' },
    { re: /^\/api\/onboarding-content/i, text: 'Updated onboarding content' },
    { re: /^\/api\/training-content/i, text: 'Updated training content' },
    { re: /^\/api\/kpi-targets/i, text: 'Updated KPI targets' },
    { re: /^\/api\/org-directory/i, text: 'Updated organisation directory' },
    { re: /^\/api\/location-scorecard\/export\/live/i, text: 'Exported location AOI scorecard (live)' },
    { re: /^\/api\/location-scorecard\/[^/]+\/export/i, text: 'Exported location AOI scorecard' },
    { re: /^\/api\/location-scorecard/i, text: 'Updated location scorecard data' },
    { re: /^\/api\/aoi/i, text: 'Updated AOI calculator data' },
    { re: /^\/api\/zoho/i, text: 'Updated Zoho CRM integration' },
];

function pathOnly(path) {
    if (!path) return '';
    const q = path.indexOf('?');
    return q >= 0 ? path.slice(0, q) : path;
}

function fromEntityAction(log) {
    const { entity, action } = log;
    if (entity === 'Auth' && action && ACTION_PHRASE[action]) {
        return ACTION_PHRASE[action];
    }
    if (!entity && !action) return null;
    const verb = ACTION_PHRASE[action] || (action ? action.replace(/_/g, ' ') : 'Changed');
    const target = ENTITY_PHRASE[entity] || (entity ? entity.toLowerCase() : 'a record');
    if (entity && action) return `${verb} ${target}`;
    if (action) return verb;
    return `Changed ${target}`;
}

function fromPath(log) {
    const p = pathOnly(log.path);
    if (!p) return null;
    for (const rule of PATH_RULES) {
        if (rule.method && rule.method !== log.method) continue;
        if (rule.re.test(p)) return rule.text;
    }
    const segment = p.replace(/^\/api\//, '').split('/').filter(Boolean)[0];
    const resource = segment ? segment.replace(/-/g, ' ') : 'record';
    const verb = { POST: 'Created', PUT: 'Updated', PATCH: 'Updated', DELETE: 'Deleted' }[log.method] || log.method || 'Changed';
    return `${verb} ${resource}`;
}

function failedSuffix(log) {
    if (!log.status || log.status < 400) return '';
    if (log.status >= 500) return ' (server error)';
    return ' (did not complete)';
}

/**
 * @returns {{ summary: string, detail: string | null }}
 */
export function describeActivity(log) {
    const detail = log.path ? pathOnly(log.path) : null;

    if (log.note?.trim()) {
        return { summary: log.note.trim() + failedSuffix(log), detail };
    }

    const fromEa = fromEntityAction(log);
    if (fromEa) {
        return { summary: fromEa + failedSuffix(log), detail };
    }

    const fromP = fromPath(log);
    if (fromP) {
        return { summary: fromP + failedSuffix(log), detail };
    }

    return { summary: 'Performed an action' + failedSuffix(log), detail };
}

export function actorIdOf(log) {
    if (!log?.actor) return null;
    if (typeof log.actor === 'object' && log.actor._id) return String(log.actor._id);
    return String(log.actor);
}
